import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import {
  Box,
  Text,
  Flex,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  SearchIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import toast from "react-hot-toast";
import { Page } from "components/page";
import { useFirebaseUser } from "utils/useFirebaseUser";
import ProtectedRoute from "components/protected-route";
import { ContactList } from "types/contactList";
import { variant } from "theme/names";
import {
  ManualFilters,
  SearchAllFields,
  FilterSection,
  CountryFilter,
  JobFilter,
  FollowersFilter,
  CompanySizeFilter,
  PostTopicFilters,
  EngagementCountFilter,
  IndustryFilter,
  JobDepartementFilter,
  JobLevelFilter,
  CompanyNameFilter,
  CompanyTagsFilter,
  DataTable,
  TEXT_COLOR_LIGHT,
  TEXT_COLOR_DARK,
} from "components/database";
import { getPlan } from "controllers/subscription";
import { UpgradePopup } from "components/upgrade-popup";
import * as analytics from "utils/analytics";
import { AiButton } from "components/common/AiButton/AiButton";
import { firebaseClient, getToken } from "firebaseClient";

export default function Database() {
  const [autoSearchValue, setAutoSearchValue] = useState<string>("");
  const [contacts, setContacts] = useState<ContactList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [manualSearchValue, setManualSearchValue] = useState<string>("");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedPostTopics, setSelectedPostTopics] = useState<string[]>([]);
  const [selectedCompanyTags, setSelectedCompanyTags] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCompanyNames, setSelectedCompanyNames] = useState<string[]>(
    []
  );
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>([]);
  const [selectedJobLevels, setSelectedJobLevels] = useState<string[]>([]);
  const [selectedJobDepartements, setSelectedJobDepartements] = useState<
    string[]
  >([]);
  const [followers, setFollowers] = useState<[string, string]>(["", ""]);
  const [companySize, setCompanySize] = useState<[string, string]>(["", ""]);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [engagementCount, setEngagementCount] = useState<[string, string]>([
    "",
    "",
  ]);
  const [noOfActiveFilters, setNoOfActiveFilters] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(50);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [pageNo, setPageNo] = useState<number>(1);
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const isAuthenticated = useFirebaseUser();
  const primaryTextColor = useColorModeValue("#2D64BC", "purple.500");
  const textColor = useColorModeValue(TEXT_COLOR_LIGHT, TEXT_COLOR_DARK);
  const isNotProUser = !getPlan(session?.user).includes("enterprise");

  const getFilters = () => {
    const filters: string[] = [];

    if (followers[0]) {
      filters.push(`followers_count > ${followers[0]}`);
    }
    if (followers[1]) {
      filters.push(`followers_count < ${followers[1]}`);
    }
    if (companySize[0]) {
      filters.push(`company_nb_employees > ${companySize[0]}`);
    }
    if (companySize[1]) {
      filters.push(`company_nb_employees < ${companySize[1]}`);
    }
    if (engagementCount[0]) {
      filters.push(`engagement_count > ${engagementCount[0]}`);
    }
    if (engagementCount[1]) {
      filters.push(`engagement_count < ${engagementCount[1]}`);
    }
    if (selectedIndustries.length > 0) {
      filters.push(`industry IN ${JSON.stringify(selectedIndustries)}`);
    }
    if (selectedJobLevels.length > 0) {
      let tempSelectedJobLevels = [...selectedJobLevels];
      if (tempSelectedJobLevels.includes("Contributor"))
        tempSelectedJobLevels.push("Employee");
      filters.push(`job_level IN ${JSON.stringify(tempSelectedJobLevels)}`);
    }
    if (selectedJobDepartements.length > 0) {
      filters.push(
        `job_departement IN ${JSON.stringify(selectedJobDepartements)}`
      );
    }
    if (selectedPostTopics.length > 0) {
      filters.push(`posts_topics IN ${JSON.stringify(selectedPostTopics)}`);
    }
    if (selectedCompanyTags.length > 0) {
      filters.push(
        `company_industry_tags IN ${JSON.stringify(selectedCompanyTags)}`
      );
    }
    if (selectedCountries.length > 0) {
      filters.push(`location_country IN ${JSON.stringify(selectedCountries)}`);
    }
    if (selectedCompanyNames.length > 0) {
      filters.push(`company_name IN ${JSON.stringify(selectedCompanyNames)}`);
    }
    if (selectedJobTitles.length > 0) {
      filters.push(`company_position IN ${JSON.stringify(selectedJobTitles)}`);
    }

    return filters.join(" AND ");
  };

  // set no. of active filters
  useEffect(() => {
    let count = 0;
    if (followers[0] !== "" || followers[1] !== "") {
      count++;
    }
    if (companySize[0] !== "" || companySize[1] !== "") {
      count++;
    }
    if (engagementCount[0] !== "" || engagementCount[1] !== "") {
      count++;
    }
    if (selectedIndustries?.length > 0) {
      count++;
    }
    if (selectedPostTopics?.length > 0) {
      count++;
    }
    if (selectedCompanyTags?.length > 0) {
      count++;
    }
    if (selectedCountries?.length > 0) {
      count++;
    }
    if (selectedJobTitles?.length > 0) {
      count++;
    }
    if (selectedCompanyNames?.length > 0) {
      count++;
    }

    setNoOfActiveFilters(count);
  }, [
    followers,
    companySize,
    engagementCount,
    selectedCompanyTags,
    selectedPostTopics,
    selectedIndustries,
    selectedCountries,
    selectedCompanyNames,
    selectedJobTitles,
  ]);

  const search = async (forcePageNo?: number) => {
    if (isNotProUser) {
      // toast.error("Feature only available to enterprise users");
      return;
    }

    setIsLoading(true);
    let search = manualSearchValue;
    let filter = getFilters();
    let url =
      "https://us-central1-ez4cast.cloudfunctions.net/linkedinAutoComplete-autoComplete2";
    url += "?search=" + encodeURIComponent(search);
    url += "&filters=" + encodeURIComponent(filter);
    url += "&limit=" + perPage;
    url += "&offset=" + ((forcePageNo ?? pageNo) - 1) * perPage;
    url += "&isInfluentLeader=true";

    // const response = await fetch(url);
    const token = await getToken(session, "autoComplete2");
    const response = await fetch(url, {
      //@ts-ignore
      headers: {
        Authorization: `Bearer ${token}`,
        tokenuserid: session?.user?.uid,
      },
    });

    const data = await response.json();

    setIsLoading(false);
    if (data.success) {
      setTotalResults(data.estimatedTotalHits ?? 0);
      let newContacts = data.results.map((contact: any) => {
        return {
          ...contact,
          followersCount: contact.followers_count,
          profileUrl: "https://www.linkedin.com/in/" + contact.username,
          companyIndustryTags: contact.company_industry_tags,
          location: {
            default: contact.location,
          },
          zFull: {
            position_groups: contact.position_groups?.map((position: any) => {
              return {
                profile_positions: [
                  {
                    title: contact.company_position,
                  },
                ],
                company: {
                  name: contact.company_name,
                },
              };
            }),
          },
        };
      });

      setContacts(newContacts);
    } else {
      toast.error(data.error ?? "An error occured");
      setContacts([]);
    }
  };

  useEffect(() => {
    if (session && isAuthenticated && !hasFetched) {
      setHasFetched(true);
      searchAuto("founders from france with more than 5k followers");
    }
  }, [session, isAuthenticated]);

  const decodeAndPrefillFilters = (filters: any) => {
    // resetting filters
    setSelectedCountries([]);
    setSelectedCompanyNames([]);
    setSelectedJobTitles([]);
    setSelectedJobLevels([]);
    setSelectedJobDepartements([]);
    setSelectedCompanyTags([]);
    setSelectedPostTopics([]);
    setSelectedIndustries([]);
    setFollowers(["", ""]);
    setCompanySize(["", ""]);
    setEngagementCount(["", ""]);

    filters?.forEach((f) => {
      if (f.var === "location_country") {
        if (f.op === "IN") {
          setSelectedCountries(f.val);
        }
        if (f.op === "=") {
          setSelectedCountries([f.val]);
        }
      }
      if (f.var === "company_name") {
        if (f.op === "IN") {
          setSelectedCompanyNames(f.val);
        }
        if (f.op === "=") {
          setSelectedCompanyNames([f.val]);
        }
      }
      if (f.var === "job_title" || f.var === "company_position") {
        if (f.op === "IN") {
          setSelectedJobTitles(f.val);
        }
        if (f.op === "=") {
          setSelectedJobTitles([f.val]);
        }
      }
      if (f.var === "job_level") {
        if (f.op === "IN") {
          setSelectedJobLevels(f.val);
        }
        if (f.op === "=") {
          setSelectedJobLevels([f.val]);
        }
      }
      if (f.var === "job_departement") {
        if (f.op === "IN") {
          setSelectedJobDepartements(f.val);
        }
        if (f.op === "=") {
          setSelectedJobDepartements([f.val]);
        }
      }
      if (f.var === "company_industry_tags") {
        if (f.op === "IN") {
          setSelectedCompanyTags(f.val);
        }
        if (f.op === "=") {
          setSelectedCompanyTags([f.val]);
        }
      }
      if (f.var === "posts_topics") {
        if (f.op === "IN") {
          setSelectedPostTopics(f.val);
        }
        if (f.op === "=") {
          setSelectedPostTopics([f.val]);
        }
      }
      if (f.var === "industry") {
        if (f.op === "IN") {
          setSelectedIndustries(f.val);
        }
        if (f.op === "=") {
          setSelectedIndustries([f.val]);
        }
      }
      if (f.var === "followers_count") {
        let value: [string, string] = ["", ""];
        if (f.op === ">") {
          value[0] = f.val;
        }
        if (f.op === "<") {
          value[1] = f.val;
        }
        setFollowers(value);
      }
      if (f.var === "company_nb_employees") {
        let value: [string, string] = ["", ""];
        if (f.op === ">") {
          value[0] = f.val;
        }
        if (f.op === "<") {
          value[1] = f.val;
        }
        setCompanySize(value);
      }
      if (f.var === "engagement_count") {
        let value: [string, string] = ["", ""];
        if (f.op === ">") {
          value[0] = f.val;
        }
        if (f.op === "<") {
          value[1] = f.val;
        }
        setEngagementCount(value);
      }
    });
  };

  const searchAuto = async (forcedSearchValue?: string) => {
    if (isNotProUser) {
      // toast.error("Feature only available to enterprise users");
      return;
    }

    setIsLoading(true);
    let url =
      "https://us-central1-ez4cast.cloudfunctions.net/linkedinAutoComplete-searchAuto";
    url += "?query=" + encodeURIComponent(forcedSearchValue ?? autoSearchValue);
    url += "&limit=" + perPage;
    url += "&isInfluentLeader=true";

    // const response = await fetch(url);
    const token = await getToken(session, "autoComplete2");
    const response = await fetch(url, {
      //@ts-ignore
      headers: {
        Authorization: `Bearer ${token}`,
        tokenuserid: session?.user?.uid,
      },
    });
    const data = await response.json();

    setIsLoading(false);

    if (data.success) {
      setManualSearchValue(data.searches.join(",") || "");
      setTotalResults(data.estimatedTotalHits ?? 0);
      decodeAndPrefillFilters(data.filters);
      let newContacts = data.results.map((contact: any) => {
        return {
          ...contact,
          followersCount: contact.followers_count,
          profileUrl: "https://www.linkedin.com/in/" + contact.username,
          companyIndustryTags: contact.company_industry_tags,
          location: {
            default: contact.location,
          },
          zFull: {
            position_groups: contact.position_groups?.map((position: any) => {
              return {
                profile_positions: [
                  {
                    title: position.position,
                  },
                ],
                company: {
                  name: position.company_name,
                },
              };
            }),
          },
        };
      });

      setContacts(newContacts);
    } else {
      toast.error(data.error ?? "An error occured");
    }
  };

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  return (
    <Page
      title="Database"
      description="Search our database for leads and people to engage with. Data enriched with AI ⚡️"
    >
      <Box filter={isNotProUser ? "blur(3px)" : "none"}>
        <Flex w={"100%"} mt={10} flexDir="column">
          <Text color={primaryTextColor} fontWeight="semibold">
            ⚡️ Describe what you’re looking for
          </Text>
          <Flex mt={2} alignItems="center" justifyContent="flex-start">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={primaryTextColor} />
              </InputLeftElement>
              <Input
                placeholder="US-based SaaS founders with over 5000 followers"
                value={autoSearchValue}
                onChange={(e) => setAutoSearchValue(e.target.value)}
              />
            </InputGroup>
            <AiButton
              containerProps={{ ml: 5 }}
              px="8"
              onClick={async () => {
                setPageNo(1);
                searchAuto();
              }}
              // variant={variant.Button.primaryAI2}
              // color="white"
              // colorScheme="none"
              isLoading={isLoading}
            >
              Go fetch it
            </AiButton>
          </Flex>
        </Flex>
        <ManualFilters
          noOfActiveFilters={noOfActiveFilters}
          leftPanel={
            <SearchAllFields>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={textColor} mb="2" />
                </InputLeftElement>
                <Input
                  size="sm"
                  placeholder="Search full profile data (name, company, headline, location...)"
                  value={manualSearchValue}
                  onChange={(e) => setManualSearchValue(e.target.value)}
                />
              </InputGroup>
            </SearchAllFields>
          }
          rightPanel={
            <>
              <FilterSection title="Profile filters">
                <CountryFilter
                  value={selectedCountries}
                  onChange={(newValue) => setSelectedCountries(newValue)}
                />
                <JobFilter
                  value={selectedJobTitles}
                  onChange={(newValue) => setSelectedJobTitles(newValue)}
                />
                <FollowersFilter
                  value={followers}
                  onApply={(newValue) => setFollowers(newValue)}
                />
                <PostTopicFilters
                  value={selectedPostTopics}
                  onChange={(newValue) => setSelectedPostTopics(newValue)}
                />
                <EngagementCountFilter
                  value={engagementCount}
                  onApply={(newValue) => setEngagementCount(newValue)}
                />
                <IndustryFilter
                  value={selectedIndustries}
                  onChange={(newValue) => setSelectedIndustries(newValue)}
                />
                <JobLevelFilter
                  value={selectedJobLevels}
                  onChange={(newValue) => setSelectedJobLevels(newValue)}
                />
                <JobDepartementFilter
                  value={selectedJobDepartements}
                  onChange={(newValue) => setSelectedJobDepartements(newValue)}
                />
              </FilterSection>
              <Box mt="2">
                <FilterSection title="Company filters">
                  <CompanyNameFilter
                    value={selectedCompanyNames}
                    onChange={(newValue) => setSelectedCompanyNames(newValue)}
                  />
                  <CompanyTagsFilter
                    value={selectedCompanyTags}
                    onChange={(newValue) => setSelectedCompanyTags(newValue)}
                  />
                  <CompanySizeFilter
                    value={companySize}
                    onApply={(newValue) => setCompanySize(newValue)}
                  />
                </FilterSection>
              </Box>
            </>
          }
          bottomPanel={
            <>
              <Flex justifyContent="flex-end" mt="4">
                <Button
                  variant="primary"
                  px="7"
                  onClick={async () => {
                    setPageNo(1);
                    search(1);
                  }}
                  isLoading={isLoading}
                >
                  Search
                </Button>
              </Flex>
              <Divider my="8" borderColor="gray.200" />
            </>
          }
        />
        <Box mt="4">
          <DataTable people={contacts} isLoading={isLoading} />
        </Box>

        <Flex mt="6" justifyContent="space-between">
          <Flex alignItems="center">
            <Menu>
              <MenuButton
                left="0"
                as={Button}
                variant="secondary"
                rightIcon={<ChevronDownIcon />}
              >
                Per page: {perPage}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => setPerPage(25)}>25</MenuItem>
                <MenuItem onClick={() => setPerPage(50)}>50</MenuItem>
                <MenuItem onClick={() => setPerPage(75)}>75</MenuItem>
                <MenuItem onClick={() => setPerPage(100)}>100</MenuItem>
              </MenuList>
            </Menu>
            {totalResults > 0 && (
              <Text ml={5} fontSize="sm">
                <i>More than {totalResults} results</i>
              </Text>
            )}
          </Flex>
          <Flex justifyContent="center">
            <Button
              mr="8"
              variant={variant.Button.secondary}
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setPageNo(pageNo - 1);
                search(pageNo - 1);
              }}
              isDisabled={pageNo === 1}
            >
              Back
            </Button>
            <Button
              variant="primary"
              rightIcon={<ArrowForwardIcon />}
              onClick={() => {
                setPageNo(pageNo + 1);
                search(pageNo + 1);
              }}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Box>
      {isNotProUser ? <UpgradePopup
        currentPlan={getPlan(session?.user)}
        feature="Database"
        availablePlans={["enterprise"]}
      /> : null}
    </Page>
  );
}
